/**
 * The Recent & pinned panel's arithmetic: buckets, ranges, timestamps, search
 * and counts (#2350 §9).
 *
 * Its own module for the reason `recentsEmptyState.ts` is: every decision here
 * is a function of a few values, and asserting them should not cost a mounted
 * drawer with a router, a query client and a pinned store. It also means the
 * DST cases and the "same title, two accounts" case can be written as data
 * rather than as a rendered tree.
 *
 * Nothing here fetches, and nothing here decides VISIBILITY. What may be shown
 * at all is `core.recall_contexts` under the caller's own RLS; this module only
 * arranges what came back.
 *
 * ── the viewer's calendar, not the server's ────────────────────────────────
 * The design says Latest is *"computed in the viewer's locale/time zone"*, and
 * every boundary below is built with `new Date(y, m, d)` — local midnight —
 * rather than by subtracting milliseconds. That is not a style choice: 24 hours
 * before local midnight is 23 or 25 hours on the two days a year the clocks
 * move, so a millisecond subtraction puts late-evening records in the wrong
 * bucket exactly when a person is most likely to notice.
 */

/** A recall row, after identity and account have been resolved through the door. */
export interface RecallRow {
  entity: string
  id: string
  /** ISO instant. Pins carry none — they are not a history. */
  at: string | null
  /** Governed title, or the neutral `Unnamed {type}` a present-but-unnamed record gets. */
  title: string
  /** Governed singular label from entity identity — never the entity KEY. */
  typeLabel: string
  iconKey: string
  /** The governed subtitle. The design's "context line". */
  context: string | null
  accountId: string | null
  accountTitle: string | null
}

export type GroupMode = 'latest' | 'type' | 'account'
export type Range = 'today' | '7d' | '30d' | 'all'

export interface RecallGroup {
  key: string
  label: string
  /** Live, and always the number of rows actually rendered under it. */
  count: number
  rows: RecallRow[]
}

/* ── local calendar boundaries ─────────────────────────────────────────────── */

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

/**
 * Which day the viewer's week starts on.
 *
 * `Intl.Locale.prototype.getWeekInfo` is the governed answer and is not
 * everywhere yet; the older `weekInfo` accessor is the same value under a
 * different name. Monday is the fallback rather than Sunday because it is the
 * ISO-8601 default and this console's deployments are European — stated here
 * rather than left as a silent constant, because it IS a guess when the runtime
 * cannot answer.
 *
 * `firstDay` is 1..7 with 7 = Sunday; `Date.getDay()` is 0..6 with 0 = Sunday.
 */
function weekStartsOn(locale?: string): number {
  try {
    const loc = new Intl.Locale(locale ?? navigator.language) as Intl.Locale & {
      getWeekInfo?: () => { firstDay: number }
      weekInfo?: { firstDay: number }
    }
    const first = loc.getWeekInfo?.().firstDay ?? loc.weekInfo?.firstDay
    if (typeof first === 'number') return first === 7 ? 0 : first
  } catch { /* an unparseable locale is not a reason to fail to draw a list */ }
  return 1
}

function startOfWeek(now: Date, locale?: string): Date {
  const first = weekStartsOn(locale)
  const today = startOfDay(now)
  const delta = (today.getDay() - first + 7) % 7
  return addDays(today, -delta)
}

export type Bucket = 'today' | 'yesterday' | 'week' | 'month' | 'older'

const BUCKET_LABEL: Record<Bucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'Earlier this week',
  month: 'Earlier this month',
  older: 'Older',
}

/**
 * Which Latest bucket an instant falls in, by the viewer's calendar.
 *
 * Order matters and the overlap is deliberate: on a Monday, "yesterday" is
 * Sunday and Sunday is BEFORE the start of this week. Testing yesterday first
 * means that row reads `Yesterday` rather than `Earlier this month`, which is
 * what a person means by it.
 */
export function bucketOf(at: string, now: Date, locale?: string): Bucket {
  const when = new Date(at)
  if (Number.isNaN(when.getTime())) return 'older'
  if (when >= startOfDay(now)) return 'today'
  if (when >= addDays(startOfDay(now), -1)) return 'yesterday'
  if (when >= startOfWeek(now, locale)) return 'week'
  if (when >= new Date(now.getFullYear(), now.getMonth(), 1)) return 'month'
  return 'older'
}

/**
 * Whether an instant is inside a range, counted in WHOLE LOCAL DAYS.
 *
 * "Last 7 days" includes today and the six days before it — a person reading
 * that control means a week of their calendar, not a rolling 168 hours, and the
 * rolling reading makes the same record appear and disappear as the clock
 * passes its own timestamp.
 */
export function inRange(at: string, now: Date, range: Range): boolean {
  if (range === 'all') return true
  const when = new Date(at)
  if (Number.isNaN(when.getTime())) return false
  const today = startOfDay(now)
  if (range === 'today') return when >= today
  return when >= addDays(today, range === '7d' ? -6 : -29)
}

/* ── timestamps ────────────────────────────────────────────────────────────── */

export interface When {
  /** Short, for the row. */
  text: string
  /** The full instant, for `title` and assistive technology. */
  exact: string
}

/**
 * The design: *"Today renders local clock time; yesterday says Yesterday;
 * recent older items use relative days; older buckets use a localized date. The
 * exact instant remains available to assistive technology/title text."*
 *
 * The seven identical `17 Sep` stamps the mock complains about are what a bare
 * date format produces for a day's work — every row the same, so the column
 * carries no information at the exact moment it is most wanted.
 */
export function formatWhen(at: string, now: Date, locale?: string): When {
  const when = new Date(at)
  if (Number.isNaN(when.getTime())) return { text: '', exact: '' }
  const exact = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeStyle: 'short' }).format(when)
  const bucket = bucketOf(at, now, locale)
  if (bucket === 'today') {
    return { text: new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(when), exact }
  }
  if (bucket === 'yesterday') return { text: 'Yesterday', exact }
  const days = Math.round((startOfDay(now).getTime() - startOfDay(when).getTime()) / 86_400_000)
  if (days <= 7) return { text: `${days}d ago`, exact }
  return { text: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(when), exact }
}

/* ── search ────────────────────────────────────────────────────────────────── */

/**
 * The design: *"Search matches resolved title, type label, governed
 * subtitle/context and resolved account title."* All four, and NOTHING ELSE —
 * in particular not `entity` and not `id`, which are the two fields on a row
 * that are raw identifiers. Matching on them would let a person find a record
 * by typing a uuid, which sounds harmless and means the identifier has become
 * part of the interface.
 *
 * Every term must match somewhere, so "quote lumen" finds the Lumen quote
 * rather than everything that is either.
 */
export function matchesQuery(row: RecallRow, query: string): boolean {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return true
  const hay = [row.title, row.typeLabel, row.context, row.accountTitle]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' ')
    .toLowerCase()
  return terms.every((term) => hay.includes(term))
}

/* ── grouping ──────────────────────────────────────────────────────────────── */

const collator = new Intl.Collator(undefined, { sensitivity: 'base' })

/**
 * Arrange rows into the visible groups for one mode.
 *
 * EMPTY GROUPS ARE ABSENT, not rendered at zero: a list of headings with
 * nothing under them is the shape the mock replaced, and a count of 0 beside a
 * heading reads as a failure rather than as an absence.
 *
 * Rows arrive newest-first (the store's order) and stay that way inside every
 * group, in every mode — so By type and By account remain a recall list rather
 * than becoming a directory.
 */
export function groupRows(rows: RecallRow[], mode: GroupMode, now: Date, locale?: string): RecallGroup[] {
  if (mode === 'latest') {
    const order: Bucket[] = ['today', 'yesterday', 'week', 'month', 'older']
    const byBucket = new Map<Bucket, RecallRow[]>()
    for (const row of rows) {
      const bucket = row.at ? bucketOf(row.at, now, locale) : 'older'
      const list = byBucket.get(bucket)
      if (list) list.push(row); else byBucket.set(bucket, [row])
    }
    return order
      .filter((bucket) => (byBucket.get(bucket)?.length ?? 0) > 0)
      .map((bucket) => ({
        key: bucket,
        label: BUCKET_LABEL[bucket],
        count: byBucket.get(bucket)!.length,
        rows: byBucket.get(bucket)!,
      }))
  }

  if (mode === 'type') {
    /* Keyed on the ENTITY, labelled with the governed singular label. Keying on
       the label would merge two entities that happen to share a word, and
       labelling with the key would put `crm_lead` on the screen. */
    const byType = new Map<string, RecallRow[]>()
    for (const row of rows) {
      const list = byType.get(row.entity)
      if (list) list.push(row); else byType.set(row.entity, [row])
    }
    return [...byType.entries()]
      .map(([entity, list]) => ({
        key: entity, label: list[0]!.typeLabel, count: list.length, rows: list,
      }))
      .sort((a, b) => collator.compare(a.label, b.label))
  }

  /* By account. The key is the governed `account_id` and NOTHING ELSE — the
     design forbids inferring one from a subtitle or from whatever organisation
     happens to be on screen. A null account is one neutral group; it is not
     "Moonbase", and it is not the name of any customer. */
  const byAccount = new Map<string, RecallRow[]>()
  for (const row of rows) {
    const key = row.accountId ?? ''
    const list = byAccount.get(key)
    if (list) list.push(row); else byAccount.set(key, [row])
  }
  const groups = [...byAccount.entries()].map(([key, list]) => ({
    key: key || 'no-account',
    /* An account the caller may not NAME keeps its grouping key and loses its
       name (the door returns account_id with a null account_title). The label
       must not fall back to the id: a uuid is never copy. */
    label: key === ''
      ? 'No account'
      : (list.find((row) => row.accountTitle)?.accountTitle ?? 'Account not shown'),
    count: list.length,
    rows: list,
  }))
  /* Named accounts alphabetically; the two neutral groups last, so a person
     scanning for a customer never has to read past them. */
  const rank = (g: RecallGroup) => (g.key === 'no-account' ? 2 : g.label === 'Account not shown' ? 1 : 0)
  return groups.sort((a, b) => rank(a) - rank(b) || collator.compare(a.label, b.label))
}

/* ── chips and counts ──────────────────────────────────────────────────────── */

export interface TypeChip { entity: string; label: string; iconKey: string; count: number }

/**
 * The type filter chips, DERIVED from the resolved rows rather than listed.
 *
 * A hard-coded Lead/Organization/… list is what the design forbids and what
 * would go stale the day a module registers an entity; deriving it also means a
 * chip can never offer a type that has nothing behind it.
 *
 * Counts come from the rows the chips are derived from — the range-filtered,
 * search-filtered set — so pressing a chip cannot produce fewer rows than its
 * own number promised.
 */
export function typeChips(rows: RecallRow[]): TypeChip[] {
  const by = new Map<string, TypeChip>()
  for (const row of rows) {
    const chip = by.get(row.entity)
    if (chip) chip.count += 1
    else by.set(row.entity, { entity: row.entity, label: row.typeLabel, iconKey: row.iconKey, count: 1 })
  }
  return [...by.values()].sort((a, b) => collator.compare(a.label, b.label))
}

/**
 * The panel title's count: the visible UNIQUE union of pins and recents.
 *
 * *"A record that is both pinned and recent counts once."* The pair is the
 * identity — the same uuid under two entities is two records, which is the same
 * rule `recents.ts` dedupes by.
 */
export function uniqueCount(...lists: { entity: string; id: string }[][]): number {
  const seen = new Set<string>()
  for (const list of lists) for (const item of list) seen.add(`${item.entity}:${item.id}`)
  return seen.size
}

/**
 * A finite, contrast-safe palette for the type chips.
 *
 * The design: *"Colour is decorative/redundant; label and icon carry the
 * meaning. Use a finite contrast-safe token palette, never entity-specific raw
 * colours."* So this returns a TONE INDEX, not a colour — the stylesheet owns
 * the values, there are six of them, and an entity the fleet adds tomorrow gets
 * one without anybody choosing it.
 *
 * Stable across renders and across sessions because it is a hash of the entity
 * key, not a position in a list: colouring by index would repaint every chip
 * the moment a person's history changed shape.
 */
export function toneOf(entity: string, tones = 6): number {
  let hash = 0
  for (let i = 0; i < entity.length; i++) hash = (hash * 31 + entity.charCodeAt(i)) >>> 0
  return hash % tones
}
